import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

// ---------------- utils ----------------
function cn(...c) { return c.filter(Boolean).join(" "); }
function pad2(n) { return String(n).padStart(2, "0"); }
function isoLocal(d) {
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`;
}
function fromIsoLocal(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}
function startOfDay(d) {
  const x = new Date(d); x.setHours(0,0,0,0); return x;
}
function endOfDay(d) {
  const x = new Date(d); x.setHours(23,59,59,999); return x;
}
// Monday-start week
function startOfWeekMon(d) {
  const x = startOfDay(d);
  const dow = x.getDay(); // 0 Sun..6 Sat
  const diff = (dow === 0 ? -6 : 1 - dow);
  x.setDate(x.getDate() + diff);
  return startOfDay(x);
}
function endOfWeekMon(d) {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  return endOfDay(e);
}
function prettyDate(d) {
  return d.toLocaleDateString("en-GB", { weekday: "short", year: "numeric", month: "short", day: "2-digit" });
}
function prettyTime(d) {
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export default function PPMPrintSchedule() {
  const todayIso = isoLocal(new Date());
  const [mode, setMode] = useState("day"); // "day" | "week"
  const [dateIso, setDateIso] = useState(todayIso);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [jobs, setJobs] = useState([]);

  const range = useMemo(() => {
    const base = fromIsoLocal(dateIso);
    if (mode === "week") {
      const s = startOfWeekMon(base);
      const e = endOfWeekMon(base);
      return { start: s, end: e };
    }
    return { start: startOfDay(base), end: endOfDay(base) };
  }, [mode, dateIso]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setErr("");
      setLoading(true);
      try {
        // Firestore range query on dueDate
        const qref = query(
          collection(db, "ppmjobs"),
          where("dueDate", ">=", Timestamp.fromDate(range.start)),
          where("dueDate", "<=", Timestamp.fromDate(range.end)),
          orderBy("dueDate", "asc")
        );

        const snap = await getDocs(qref);
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        if (cancelled) return;
        setJobs(rows);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load jobs.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [range.start, range.end]);

  const grouped = useMemo(() => {
    const map = new Map();

    for (const j of jobs) {
      const engId = j.assignedEngineerId || "unassigned";
      const engName = j.assignedEngineerName || "Unassigned";
      const key = `${engId}__${engName}`;

      if (!map.has(key)) map.set(key, { engId, engName, items: [] });
      map.get(key).items.push(j);
    }

    // sort engineers by name
    return Array.from(map.values()).sort((a, b) => String(a.engName).localeCompare(String(b.engName)));
  }, [jobs]);

  const heading = useMemo(() => {
    if (mode === "week") {
      return `Week: ${prettyDate(range.start)} → ${prettyDate(range.end)}`;
    }
    return `Day: ${prettyDate(range.start)}`;
  }, [mode, range.start, range.end]);

  function handlePrint() {
    window.print();
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Print CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { page-break-after: always; }
          .print-page:last-child { page-break-after: auto; }
          body { background: white !important; }
        }
      `}</style>

      {/* Controls */}
      <div className="no-print flex flex-col md:flex-row md:items-end gap-3 justify-between mb-4">
        <div>
          <div className="text-2xl font-extrabold text-gray-900">PPM Schedule Print</div>
          <div className="text-sm text-gray-600">{heading}</div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div>
            <div className="text-xs font-semibold text-gray-500">View</div>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
            </select>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500">
              {mode === "week" ? "Any date in week" : "Date"}
            </div>
            <input
              type="date"
              value={dateIso}
              onChange={(e) => setDateIso(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2"
            />
          </div>

          <button
            onClick={handlePrint}
            className="rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2"
          >
            Print
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-600">Loading…</div>
      ) : err ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
          <div className="font-bold">Problem</div>
          <div className="mt-1">{err}</div>
        </div>
      ) : grouped.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-gray-600">
          No jobs in this range.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((g) => (
            <div key={`${g.engId}__${g.engName}`} className="print-page">
              {/* Engineer header */}
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold text-gray-500">Engineer</div>
                    <div className="text-2xl font-extrabold text-gray-900">{g.engName}</div>
                    <div className="text-sm text-gray-600 mt-1">{heading}</div>
                  </div>
                  <div className="text-sm font-semibold text-gray-700">
                    Jobs: {g.items.length}
                  </div>
                </div>

                {/* Simple list/table */}
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-2">Due</th>
                        <th className="py-2 pr-2">Machine</th>
                        <th className="py-2 pr-2">Task</th>
                        <th className="py-2 pr-2">Status</th>
                        <th className="py-2 pr-2">Sign-off</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((j) => {
                        const d = j.dueDate?.toDate ? j.dueDate.toDate() : null;
                        const dueStr =
                          mode === "week" && d ? `${prettyDate(d)} ${prettyTime(d)}` :
                          d ? prettyTime(d) : "—";

                        const machineNumber = j.machineNumber || j.assetNumber || j.assetCode || "—";
                        const machineName = j.assetName || "";

                        const machine = machineName
                          ? `${machineNumber} - ${machineName}`
                          : machineNumber;
                        const task = j.taskName || "—";
                        const status = j.status || "open";

                        return (
                          <tr key={j.id} className="border-b last:border-b-0">
                            <td className="py-2 pr-2 whitespace-nowrap">{dueStr}</td>
                            <td className="py-2 pr-2">{machine}</td>
                            <td className="py-2 pr-2">{task}</td>
                            <td className="py-2 pr-2 capitalize">{status}</td>
                            <td className="py-2 pr-2">
                              <div className="h-8 border border-gray-300 rounded-md" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Notes box */}
                <div className="mt-4">
                  <div className="text-xs font-semibold text-gray-500">Notes</div>
                  <div className="h-16 border border-gray-300 rounded-md mt-1" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* tiny footer (print will include it) */}
      <div className="mt-6 text-xs text-gray-400">
        Generated: {new Date().toLocaleString("en-GB")}
      </div>
    </div>
  );
}
